import { useRef, useState } from "react";
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import classNames from 'classnames';
import Media from 'components/Media';
import 'assets/styles/WorkPreview.css';
import useCustomCursor from "./CustomCursor";

export default function WorkPreview({ media, mediaHover, tags = [], title, description, link, section }) {
  const isHomePage = window.location.pathname === "/";
  const HeadingTag = isHomePage ? motion.h3 : motion.h2;

  const videoRef = useRef(null);
  const { x, y } = useCustomCursor();
  const [showCursor, setShowCursor] = useState(false);
  const [hover, setHover] = useState(false);
  const FADE_MS = 350;
  const safeTags = Array.isArray(tags) ? tags : [];

  const handleEnter = () => {
    setHover(true);
    setShowCursor(true);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        const p = videoRef.current.play();
        if (p && typeof p.then === 'function') p.catch(() => {});
      } catch {}
    }
  };

  const handleLeave = () => {
    setHover(false);
    setShowCursor(false);
    if (videoRef.current) {
      setTimeout(() => {
        try {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        } catch {}
      }, FADE_MS);
    }
  };

  return (
    <div className={classNames('work-preview', { 'works': section })}>
      <Link
        to={`/work/${link}`}
        className="work-image group relative cursor-none"
        tabIndex={-1}
        aria-hidden="true"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Media source={media} className="w-full h-full object-cover block" />

        {mediaHover && (
          <motion.video
            ref={videoRef}
            src={mediaHover}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: hover ? 1 : 0 }}
            transition={{ duration: FADE_MS / 1000, ease: "easeInOut" }}
          />
        )}

        <span className="sr-only">{title}</span>
      </Link>

      <div className="work-content">
        <>
          {safeTags.map(tag => <span key={tag} className="tag-sm">{tag}</span>)}
        </>
        <HeadingTag
          className="k-title-sm"
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Link to={`/work/${link}`} className="link-animated">
            {title}
            <span className="sr-only"> - Read more</span>
          </Link>
        </HeadingTag>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <PortableText
            value={description}
            components={{
              block: {
                normal: ({ children }) => <p className="k-text-lg">{children}</p>,
              },
            }}
          />
        </motion.div>
      </div>

      <AnimatePresence>
        {showCursor && (
          <motion.div
            key="cursor"
            className="cursor-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ top: y, left: x }}
          >
            View Work
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
